import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Package, TrendingUp, Shield, Users, Wallet } from 'lucide-react';
import { features } from '../data/mock';

const iconMap = {
  Sparkles,
  Package,
  TrendingUp,
  Shield,
  Users,
  Wallet
};

const FeatureCard = ({ feature, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);
  const Icon = iconMap[feature.icon];

  useEffect(() => {
    const currentRef = cardRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setIsVisible(true);
          }, index * 100);
        }
      },
      { threshold: 0.2 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [index]);

  return (
    <div
      ref={cardRef}
      className={`group bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8 transition-all duration-500 hover:transform hover:translate-y-[-8px] hover:border-[rgb(197,155,47)] hover:shadow-[0_20px_40px_rgba(197,155,47,0.2)] relative overflow-hidden ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`}
    >
      {/* Top accent border with gradient */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[rgb(124,56,178)] via-[rgb(197,155,47)] to-[rgb(124,56,178)] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

      {/* Icon with purple-gold gradient */}
      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[rgba(197,155,47,0.2)] to-[rgba(124,56,178,0.2)] border border-[rgba(197,155,47,0.3)] flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
        <Icon size={28} className="text-[rgb(197,155,47)]" />
      </div>

      {/* Content */}
      <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-[rgb(197,155,47)] transition-colors duration-300">
        {feature.title}
      </h3>
      <p className="text-[rgb(161,161,170)] leading-relaxed">
        {feature.description}
      </p>
    </div>
  );
};

const Features = () => {
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    const currentRef = headerRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsHeaderVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <section id="why-choose-us" className="py-24 px-6 bg-[rgb(17,17,19)] relative overflow-hidden">
      {/* Background accent - Purple glow */}
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[rgba(124,56,178,0.05)] blur-[100px] rounded-full"></div>

      <div className="max-w-[1200px] mx-auto relative z-10">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-700 ${
            isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center space-x-2 text-[rgb(197,155,47)] text-sm font-semibold mb-4">
            <div className="w-8 h-[2px] bg-gradient-to-r from-transparent to-[rgb(197,155,47)]"></div>
            <span>WHY CHOOSE US</span>
            <div className="w-8 h-[2px] bg-gradient-to-l from-transparent to-[rgb(197,155,47)]"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            The <span className="bg-gradient-to-r from-[rgb(197,155,47)] to-[rgb(224,194,95)] text-transparent bg-clip-text">Eshwald Ops</span> Advantage
          </h2>
          <p className="text-xl text-[rgb(218,218,218)] max-w-[700px] mx-auto">
            We handle the business complexity so you can focus on what you do best — creating content your audience loves.
          </p>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <FeatureCard key={feature.id} feature={feature} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
